/**
 * Tax Service
 *
 * Type-safe tax management service using @monorepo/shared schemas
 */

import {
  type CreateTax,
  CreateTaxSchema,
  type PaginatedTaxesResponse,
  PaginatedTaxesResponseSchema,
  type TaxQuery,
  type TaxResponse,
  TaxResponseSchema,
  type UpdateTax,
  UpdateTaxSchema,
} from '@monorepo/shared/schemas/taxes'
import { del, get, post, put } from '@/lib/api-client'

export const taxService = {
  /**
   * List all taxes (filtered by query)
   */
  async getTaxes(query?: TaxQuery): Promise<PaginatedTaxesResponse> {
    const queryParams = new URLSearchParams()

    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())
    if (query?.search) queryParams.append('search', query.search)
    if (query?.code) queryParams.append('code', query.code)
    if (query?.tax_type && query.tax_type !== 'all')
      queryParams.append('tax_type', query.tax_type)
    if (query?.jurisdiction)
      queryParams.append('jurisdiction', query.jurisdiction)
    if (query?.jurisdiction_level)
      queryParams.append('jurisdiction_level', query.jurisdiction_level)
    if (query?.is_active !== undefined)
      queryParams.append('is_active', query.is_active.toString())
    if (query?.effective_date)
      queryParams.append('effective_date', query.effective_date)
    if (query?.sort_by) queryParams.append('sort_by', query.sort_by)
    if (query?.sort_order) queryParams.append('sort_order', query.sort_order)

    const url = `/taxes${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedTaxesResponseSchema)
  },

  /**
   * Get tax by ID
   */
  async getTax(id: string): Promise<TaxResponse> {
    return get(`/taxes/${id}`, TaxResponseSchema)
  },

  /**
   * Create new tax
   */
  async createTax(data: CreateTax): Promise<TaxResponse> {
    const validated = CreateTaxSchema.parse(data)

    return post('/taxes', validated, TaxResponseSchema)
  },

  /**
   * Update tax
   */
  async updateTax(id: string, data: UpdateTax): Promise<TaxResponse> {
    const validated = UpdateTaxSchema.parse(data)

    return put(`/taxes/${id}`, validated, TaxResponseSchema)
  },

  /**
   * Delete tax (marks as inactive)
   */
  async deleteTax(id: string): Promise<void> {
    await del(`/taxes/${id}`)
  },
}

export default taxService
